import { useState, useMemo, useCallback } from 'react';
import { useDisclosure, useColorModeValue } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/sdk/auth/hooks';
import { helpers } from '@/client/utils/helpers';
import { useListUserBots, useDeleteUserBot } from '@/lib/sdk/adhoc/queries';
import { useTeamMembers } from '@/lib/sdk/team/queries';
import type { UserBot } from '@/lib/sdk/adhoc/types';

// sopOnly narrows the includeSop=true result to just the plain SOP checklist
// bots, excluding the instant-task bots that query also (necessarily)
// returns — used by the Tasks page's Checklists tab (#681 3-way split: Team
// Tasks / Checklists / Others), which needs the SOP-only subset of the same
// aggregate data Team Tasks shows for instant bots.
export function useUserBotsPage(includeSop?: boolean, sopOnly?: boolean) {
  const { currentOrg, user, featurePermissions, userPermission } = useAuth();
  const navigate = useNavigate();
  const orgId = currentOrg?.id ?? '';

  const isSuperAdmin = user?.is_super_admin === true;
  const canAdd    = helpers.hasFeatureAction(featurePermissions, userPermission, isSuperAdmin, 'workflows', 'add');
  const canEdit   = helpers.hasFeatureAction(featurePermissions, userPermission, isSuperAdmin, 'workflows', 'edit');
  const canDelete = helpers.hasFeatureAction(featurePermissions, userPermission, isSuperAdmin, 'workflows', 'delete');
  const canViewInstant = helpers.hasFeatureAction(featurePermissions, userPermission, isSuperAdmin, 'workflows', 'view_instant');

  const { data, isLoading, refetch, isRefetching } = useListUserBots(orgId, includeSop);
  const events = useMemo(
    () => (sopOnly ? (data?.items ?? []).filter((e) => !e.adhoc_tasks_enabled) : data?.items ?? []),
    [data, sopOnly]
  );

  const deleteUserBot = useDeleteUserBot();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();

  const [deleteTarget, setDeleteTarget] = useState<UserBot | null>(null);
  const [addTasksBot, setAddTasksBot] = useState<UserBot | null>(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const { data: teamData } = useTeamMembers({ org_id: orgId, limit: 500, offset: 0 });
  const teamMembers = teamData?.items ?? [];

  const cardGradient = useColorModeValue(
    'linear(to-br, white, brand.50)',
    'linear(to-br, gray.800, gray.700)'
  );

  const handleDelete = useCallback((event: UserBot) => {
    setDeleteTarget(event);
    onDeleteOpen();
  }, [onDeleteOpen]);

  const handleDeleteConfirm = useCallback(() => {
    if (!deleteTarget) return;
    deleteUserBot.mutate({ orgId, botCode: deleteTarget.short_code! }, {
      onSettled: () => onDeleteClose(),
    });
  }, [deleteTarget, orgId, deleteUserBot, onDeleteClose]);

  const summaryStats = useMemo(() => {
    const totalTasks          = events.reduce((s, e) => s + (e.total_count ?? 0), 0);
    const totalCompleted      = events.reduce((s, e) => s + (e.completed_count ?? 0), 0);
    const totalOverdue        = events.reduce((s, e) => s + (e.overdue_count ?? 0), 0);
    const totalDueToday       = events.reduce((s, e) => s + (e.due_today_count ?? 0), 0);
    const totalCompletedToday = events.reduce((s, e) => s + (e.completed_today_count ?? 0), 0);
    const overdueEventCount        = events.filter(e => (e.overdue_count ?? 0) > 0).length;
    const dueTodayEventCount       = events.filter(e => (e.due_today_count ?? 0) > 0).length;
    const completedTodayEventCount = events.filter(e => (e.completed_today_count ?? 0) > 0).length;
    const overallPct = totalTasks > 0 ? Math.round((totalCompleted / totalTasks) * 100) : 0;
    return { totalTasks, totalCompleted, totalOverdue, totalDueToday, totalCompletedToday, overdueEventCount, dueTodayEventCount, completedTodayEventCount, overallPct };
  }, [events]);

  return {
    orgId, navigate,
    canAdd, canEdit, canDelete, canViewInstant,
    events, isLoading, refetch, isRefetching,
    isDeleteOpen, onDeleteClose,
    deleteTarget,
    addTasksBot, setAddTasksBot,
    isCreateOpen, setIsCreateOpen,
    teamMembers,
    handleDelete, handleDeleteConfirm,
    isPendingDelete: deleteUserBot.isPending,
    summaryStats,
    cardGradient,
  };
}
